import { useCallback, useEffect, useState } from "react"
import { useMap, Marker } from "react-map-gl/mapbox"
import { debounce } from "lodash"
import type { IDPE } from "@/types/explore"

const DPE_COLORS: Record<string, string> = {
  A: "#009C6C",
  B: "#52B153",
  C: "#A2CD68",
  D: "#F5EB42",
  E: "#F0B040",
  F: "#EB6E32",
  G: "#D7221F",
}

const DPEMarker = ({ dpe }: { dpe?: string }) => {
  const color = dpe ? DPE_COLORS[dpe] || "#999" : "#999"
  return (
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: color,
        border: "2px solid white",
        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        fontWeight: 700,
        color: "#fff",
      }}
    >
      {dpe}
    </div>
  )
}

interface DPELayerProps {
  onMarkerClick?: (dpe: IDPE) => void
  dpeFilter?: string[]
}

export const DPELayer = ({ onMarkerClick, dpeFilter }: DPELayerProps) => {
  const { current: map } = useMap()
  const [dpeMarkers, setDpeMarkers] = useState<IDPE[]>([])

  function getDPEInfo() {
    if (!map) return
    const bounds = map.getBounds()
    if (!bounds) return
    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()

    let apiUrl = `https://data.ademe.fr/data-fair/api/v1/datasets/dpe03existant/lines?size=300&bbox=${sw.lng}%2C+${sw.lat}%2C+${ne.lng}%2C+${ne.lat}`

    if (dpeFilter && dpeFilter.length > 0) {
      const filterQueries = dpeFilter.map((filter) => `etiquette_dpe:"${filter}"`).join(" OR ")
      apiUrl += `&qs=${encodeURIComponent(filterQueries)}`
    }

    fetch(apiUrl)
      .then((response) => response.json())
      .then((data) => {
        setDpeMarkers(
          data?.results
            .filter((dpe: Record<string, unknown>) => dpe?.etiquette_dpe !== "N" && dpe["_geopoint"])
            .map((dpe: Record<string, unknown>) => {
              const coordinates = (dpe["_geopoint"] as string).split(",")
              return {
                ...dpe,
                latitude: parseFloat(coordinates[0]),
                longitude: parseFloat(coordinates[1]),
              }
            }),
        )
      })
      .catch((error) => console.error("Error:", error))
  }

  const debouncedGetDPEInfo = useCallback(
    debounce(() => {
      getDPEInfo()
    }, 300),
    [dpeFilter],
  )

  useEffect(() => {
    if (map) {
      map.on("moveend", debouncedGetDPEInfo)
      getDPEInfo()
    }

    return () => {
      if (map) {
        map.off("moveend", debouncedGetDPEInfo)
      }
    }
  }, [map, debouncedGetDPEInfo, dpeFilter])

  return (
    <>
      {dpeMarkers.map((marker, index: number) => (
        <Marker
          key={index}
          longitude={marker.longitude!}
          latitude={marker.latitude!}
          onClick={(e) => {
            e.originalEvent.stopPropagation()
            onMarkerClick?.(marker)
          }}
        >
          <DPEMarker dpe={marker.etiquette_dpe} />
        </Marker>
      ))}
    </>
  )
}
